import React, { FunctionComponent, useContext, useState } from "react";
import { useTranslate } from "../../../helpers";
import { County } from "../../../@types";
import { Button } from "@cmsgov/ds-medicare-gov";
import { StateChoiceList } from "../../../components";
import CountySearchField, {
  SearchFunction,
} from "../../../components/CountySearchField";
import { AppContext } from "../../../app/store";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../../app/contexts/Analytics/types";

interface StatePickerProps {
  className?: string;
  onStateSelection: (county: County, zipcode: string) => void;
}

const ChangeLocationStatePicker: FunctionComponent<StatePickerProps> = ({
  className = "",
  onStateSelection,
}) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const [
    countySearchFunction,
    setCountySearchFunction,
  ] = useState<null | SearchFunction>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [counties, setCounties] = useState<County[]>([]);

  const [validZipNotEntered, setValidZipNotEntered] = useState<boolean>(false);
  const [stateNotSelectedError, setStateNotSelectedError] = useState<boolean>(
    false
  );
  const [selectedZipcode, setSelectedZipcode] = useState<string>("");
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);

  return (
    <div className={`ChangeLocationStatePicker ${className}`}>
      <span className="ChangeLocationStatePicker__subtitle">
        {!!counties.length && t("state_picker.select_state_for_zip")}
      </span>
      {!counties.length ? (
        <CountySearchField
          autoSearchOnReady={false}
          hasInvalidZipError={validZipNotEntered}
          onSearchError={(): void => {
            setSearching(false);
            setCountySearchFunction(null);
          }}
          onSearchReady={(search: SearchFunction): void => {
            setValidZipNotEntered(false);
            setCountySearchFunction(() => search);
          }}
          onSearching={(): void => {
            setSearching(true);
          }}
          onSearchComplete={(
            countyResults: County[],
            zipcode: string
          ): void => {
            setSearching(false);
            setSelectedZipcode(zipcode);
            setCounties(countyResults);
          }}
        />
      ) : (
        <StateChoiceList
          countyList={counties}
          onSingleState={(selection: County): void => {
            onStateSelection(selection, selectedZipcode);
          }}
          onStateSelection={(selection: County): void => {
            setStateNotSelectedError(false);
            setSelectedCounty(selection);
          }}
          hasError={stateNotSelectedError}
        />
      )}
      <Button
        variation="solid"
        onClick={(): void => {
          // If a search function is not available, the zipcode has not yet been validated
          setValidZipNotEntered(countySearchFunction === null);
          dispatch({
            type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
            settings: {
              button: {
                buttonStyle: AnalyticsButtonStyle.PRIMARY,
                buttonType: AnalyticsButtonType.BUTTON,
                text: t("medigap.change_location.button_text"),
              },
            },
          });

          // If there isn't a search already in-progress, perform county search
          if (countySearchFunction && !searching) {
            countySearchFunction();
          }

          // If the view is the state choice list and we clicked update without making a selection
          if (!!counties.length && !selectedCounty) {
            setStateNotSelectedError(true);
          }

          // Ivoke the onStateSelection handler if a state selection has been made
          if (selectedZipcode && selectedCounty) {
            onStateSelection(selectedCounty, selectedZipcode);
          }
        }}
      >
        {t("medigap.change_location.button_text")}
      </Button>
    </div>
  );
};

export default ChangeLocationStatePicker;
